import type { EventBus } from "../core/EventBus.ts";
import { DEFAULT_INITIALS } from "../core/initials.ts";

const COMBO_TIMEOUT = 2000;
const COMBO_THRESHOLDS = [2, 5, 10, 20, 35, 50];
const COMBO_MULTIPLIERS = [1, 2, 3, 4, 5, 7, 10];

/** D-322 / AC-5 (Task-007) — the combo tuning block, data-driven via
 *  `waves.json` `score`. Resolved via `resolveScoreConfig`. */
export interface ScoreConfig {
  comboTimeoutMs: number;
  comboThresholds: number[];
  comboMultipliers: number[];
}

/** The code default — byte-identical to the pre-D-322 module constants. */
export const DEFAULT_SCORE_CONFIG: ScoreConfig = {
  comboTimeoutMs: COMBO_TIMEOUT,
  comboThresholds: COMBO_THRESHOLDS,
  comboMultipliers: COMBO_MULTIPLIERS,
};

/** Merge an optional (data-driven) combo override onto the code default.
 *  Absent/empty → the default unchanged (the byte-identical fallback). */
export function resolveScoreConfig(override?: {
  comboTimeoutMs?: number;
  comboThresholds?: number[];
  comboMultipliers?: number[];
} | null): ScoreConfig {
  if (!override) return DEFAULT_SCORE_CONFIG;
  return {
    comboTimeoutMs: override.comboTimeoutMs ?? DEFAULT_SCORE_CONFIG.comboTimeoutMs,
    comboThresholds:
      override.comboThresholds ?? DEFAULT_SCORE_CONFIG.comboThresholds,
    comboMultipliers:
      override.comboMultipliers ?? DEFAULT_SCORE_CONFIG.comboMultipliers,
  };
}

export class ScoreSystem {
  private score_ = 0;
  private displayScore_ = 0;
  private combo_ = 0;
  private comboTimer = 0;
  private comboMultiplier_ = 1;
  // Exposed for reading in future UI
  waveScore_ = 0;
  private killStreak = 0;
  private maxCombo_ = 0;
  private bus: EventBus;
  private scoreMultiplier_ = 1;
  private comboTimeoutMultiplier_ = 1;
  private frozen_ = false;
  private unsubscribers: (() => void)[] = [];
  private highscores_: {
    score: number;
    wave: number;
    combo: number;
    name: string;
    date: string;
  }[] = [];
  /** D-322 / AC-5 (Task-007) — the resolved combo tuning, data-driven via
   *  `waves.json` `score`. Default = byte-identical pre-D-322 constants. */
  private readonly comboConfig: ScoreConfig;

  constructor(bus: EventBus, comboConfig: ScoreConfig = DEFAULT_SCORE_CONFIG) {
    this.bus = bus;
    this.comboConfig = comboConfig;

    this.unsubscribers.push(
      bus.on("enemy.killed", (data: unknown) => {
        this.onKill(data as { enemy: { reward: number } });
      }),
    );

    this.unsubscribers.push(
      bus.on("wave.completed", (data: unknown) => {
        this.onWaveComplete(data as { wave: number });
      }),
    );

    this.unsubscribers.push(
      bus.on("synergy.triggered", () => {
        this.addScore(15, "synergy");
      }),
    );
  }

  destroy(): void {
    for (const unsub of this.unsubscribers) unsub();
    this.unsubscribers.length = 0;
  }

  get score(): number {
    return this.score_;
  }
  get displayScore(): number {
    return this.displayScore_;
  }
  get combo(): number {
    return this.combo_;
  }
  get comboMultiplier(): number {
    return this.comboMultiplier_;
  }
  get maxCombo(): number {
    return this.maxCombo_;
  }
  get highscores(): readonly {
    score: number;
    wave: number;
    combo: number;
    name: string;
    date: string;
  }[] {
    return this.highscores_;
  }

  set scoreMultiplier(val: number) {
    this.scoreMultiplier_ = val;
  }

  get scoreMultiplier(): number {
    return this.scoreMultiplier_;
  }

  set comboTimeoutMultiplier(val: number) {
    this.comboTimeoutMultiplier_ = val;
  }

  get comboTimeoutMultiplier(): number {
    return this.comboTimeoutMultiplier_;
  }

  /**
   * BL-63 — the fraction (0..1) of the combo window still open, for the HUD's
   * multiplier-transparency timer. Render-only: a PURE read over the existing
   * private `comboTimer` against the active window (`COMBO_TIMEOUT` × the skill
   * timeout multiplier). It NEVER advances or mutates the sim — the timer is
   * still driven solely by `update(dt)` — so the determinism baseline stays
   * byte-unchanged. 0 when no chain is live.
   */
  comboWindowRemaining(): number {
    if (this.combo_ <= 0 || this.comboTimer <= 0) return 0;
    const window = this.comboConfig.comboTimeoutMs * this.comboTimeoutMultiplier_;
    if (!(window > 0)) return 0;
    const r = this.comboTimer / window;
    return r < 0 ? 0 : r > 1 ? 1 : r;
  }

  get frozen(): boolean {
    return this.frozen_;
  }

  freeze(): void {
    this.frozen_ = true;
  }

  update(dt: number): void {
    if (this.comboTimer > 0) {
      this.comboTimer -= dt;
      if (this.comboTimer <= 0) this.resetCombo();
    }

    if (this.displayScore_ < this.score_) {
      const diff = this.score_ - this.displayScore_;
      const step = Math.max(1, Math.floor(diff * 0.15));
      this.displayScore_ = Math.min(this.displayScore_ + step, this.score_);
    }
  }

  addLivesBonus(lives: number, initialHp: number): number {
    if (this.frozen_) return 0;
    const BONUS_PCT = 0.15;
    const fraction = Math.max(0, Math.min(lives / initialHp, 1));
    const bonus = Math.round(this.score_ * BONUS_PCT * fraction);
    if (bonus <= 0) return 0;
    this.addScore(bonus, "lives");
    return bonus;
  }

  isHighscore(): boolean {
    if (this.highscores_.length < 5) return true;
    const last = this.highscores_[this.highscores_.length - 1];
    return last !== undefined ? this.score_ > last.score : true;
  }

  saveHighscore(
    wave: number,
    name = DEFAULT_INITIALS,
  ): {
    score: number;
    wave: number;
    combo: number;
    name: string;
    date: string;
  } {
    const entry = {
      score: this.score_,
      wave,
      combo: this.maxCombo_,
      name,
      date: new Date().toISOString().slice(0, 10),
    };

    this.highscores_.push(entry);
    this.highscores_.sort((a, b) => b.score - a.score);
    this.highscores_ = this.highscores_.slice(0, 5);

    return entry;
  }

  reset(): void {
    this.score_ = 0;
    this.displayScore_ = 0;
    this.combo_ = 0;
    this.comboTimer = 0;
    this.comboMultiplier_ = 1;
    this.waveScore_ = 0;
    this.killStreak = 0;
    this.maxCombo_ = 0;
    this.scoreMultiplier_ = 1;
    this.comboTimeoutMultiplier_ = 1;
    this.frozen_ = false;
  }

  private onKill(data: { enemy: { reward: number } }): void {
    const baseScore = data.enemy.reward ?? 10;

    this.combo_++;
    this.killStreak++;
    this.comboTimer = this.comboConfig.comboTimeoutMs * this.comboTimeoutMultiplier_;

    const thresholds = this.comboConfig.comboThresholds;
    const multipliers = this.comboConfig.comboMultipliers;
    this.comboMultiplier_ = multipliers[0]!;
    for (let i = 0; i < thresholds.length; i++) {
      if (this.combo_ >= thresholds[i]!) {
        this.comboMultiplier_ = multipliers[i + 1]!;
      }
    }

    if (this.combo_ > this.maxCombo_) this.maxCombo_ = this.combo_;

    const points = baseScore * this.comboMultiplier_;
    this.addScore(points, "kill");

    for (let i = 0; i < thresholds.length; i++) {
      if (this.combo_ >= thresholds[i]! && this.combo_ - 1 < thresholds[i]!) {
        this.bus.emit("score.combo", { level: i + 2, combo: this.combo_ });
      }
    }
  }

  private onWaveComplete(data: { wave: number }): void {
    const wave = data.wave ?? 1;
    const waveBonus = wave * 100;
    this.waveScore_ = waveBonus;
    this.addScore(waveBonus, "wave");
    this.bus.emit("score.waveBonus", { wave, bonus: waveBonus });
  }

  private addScore(points: number, source: string): void {
    if (this.frozen_) return;
    const adjusted = Math.round(points * this.scoreMultiplier_);
    this.score_ += adjusted;
    this.bus.emit("score.changed", {
      score: this.score_,
      delta: adjusted,
      source,
      combo: this.combo_,
      multiplier: this.comboMultiplier_,
    });
  }

  private resetCombo(): void {
    if (this.combo_ > 1) {
      this.bus.emit("score.comboEnd", {
        combo: this.combo_,
        multiplier: this.comboMultiplier_,
      });
    }
    this.combo_ = 0;
    this.comboMultiplier_ = 1;
    this.comboTimer = 0;
  }
}
